// ClientHealthScoreBadge — blueprint module #18 Book of Business
// Health Score, applied to a single client.
//
// Each client gets a 0-100 score across seven dimensions the blueprint
// calls out:
//   1. Communication         — recent contact within 30 days?
//   2. Renewal readiness     — compliance items complete?
//   3. Referral likelihood   — long-tenured + satisfied signal?
//   4. Cross-sell potential  — missing coverage lines?
//   5. Compliance completeness — pull-through of module #4 score
//   6. Payment stability     — active status, no lapse in last 90d?
//   7. Engagement            — inbound message reply rate?
//
// Computed CLIENT-SIDE from data the modal has already fetched
// (policies, plus a small extra query to count messages). No new
// schema, no server round-trip beyond what other panels already do.
//
// Renders as a compact seven-bar breakdown + total. The panel does
// NOT block anything — it's diagnostic. Module #4 is what enforces
// blocking; this one surfaces WHY a book is healthy or fragile.

import React from 'react';
import { supabase } from '../lib/supabase';

// Score a dimension: pass an array of {points, max} contributions.
function score(contributions) {
  const total = contributions.reduce((s, c) => s + c.points, 0);
  const max   = contributions.reduce((s, c) => s + c.max,    0);
  return max > 0 ? Math.round((total / max) * 100) : 0;
}

export default function ClientHealthScoreBadge({ clientId, ownerId, policies = [], complianceScore = null }) {
  const [msgSummary, setMsgSummary] = React.useState(null);

  // Small side query — how many inbound + outbound messages, and when
  // was the last inbound? Used by Communication + Engagement.
  React.useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: msgs } = await supabase
          .from('messages')
          .select('direction, sent_at')
          .eq('lead_id', String(clientId))
          .order('sent_at', { ascending: false })
          .limit(500);
        if (cancelled) return;
        const inbound  = (msgs || []).filter(m => m.direction === 'in').length;
        const outbound = (msgs || []).filter(m => m.direction === 'out').length;
        const lastAny  = msgs?.[0]?.sent_at || null;
        const lastIn   = (msgs || []).find(m => m.direction === 'in')?.sent_at || null;
        setMsgSummary({ inbound, outbound, lastAny, lastIn });
      } catch {
        setMsgSummary({ inbound: 0, outbound: 0, lastAny: null, lastIn: null });
      }
    })();
    return () => { cancelled = true; };
  }, [clientId, ownerId]);

  if (!msgSummary) return <div className="text-xs text-slate-500 italic px-1 py-2">Scoring…</div>;

  const now = Date.now();
  const daysAgo = (iso) => iso ? Math.max(0, Math.round((now - new Date(iso).getTime()) / 86_400_000)) : Infinity;

  const activePolicies = policies.filter(p => p.status === 'active');
  const lapsedPolicies = policies.filter(p => p.status === 'lapsed' || p.status === 'terminated');
  const earliestActive = activePolicies
    .map(p => p.effective_date)
    .filter(Boolean)
    .sort()[0];
  const tenureDays = earliestActive ? daysAgo(earliestActive) : 0;

  // 1. Communication — 100 if contacted within 7d, tapered to 0 at 90d.
  const daysSinceLastAny = daysAgo(msgSummary.lastAny);
  const commScore = daysSinceLastAny <= 7  ? 100
                  : daysSinceLastAny <= 30 ? 80
                  : daysSinceLastAny <= 60 ? 50
                  : daysSinceLastAny <= 90 ? 25
                  :                          0;

  // 2. Renewal readiness — pulls compliance score directly (if
  // module #4 has scored this client). Fallback: 60 (unknown).
  const renewalScore = complianceScore == null ? 60 : complianceScore;

  // 3. Referral likelihood — has tenure > 6mo, active book, recent
  // contact. Loose signal; the real "likelihood" would ML-score.
  const referralScore = score([
    { points: tenureDays >= 180 ? 40 : Math.round((tenureDays / 180) * 40), max: 40 },
    { points: activePolicies.length > 0 ? 30 : 0, max: 30 },
    { points: daysSinceLastAny <= 60 ? 30 : 0, max: 30 },
  ]);

  // 4. Cross-sell potential — MORE gaps = MORE potential. Loose
  // signal: coverage types present out of 5 common (health, dental,
  // vision, life, ancillary).
  const products = new Set(activePolicies.map(p => String(p.product_type || p.line_of_business || '').toLowerCase()));
  const missingCount = ['health','dental','vision','life','ancillary'].filter(t => !Array.from(products).some(p => p.includes(t))).length;
  const crosssellScore = Math.min(100, missingCount * 22);

  // 5. Compliance completeness — direct pull-through of module #4.
  const complianceCompletenessScore = complianceScore == null ? 60 : complianceScore;

  // 6. Payment stability — active policies vs lapsed ratio + no
  // recent lapse.
  const paymentScore = score([
    { points: activePolicies.length > 0 ? 60 : 0, max: 60 },
    { points: lapsedPolicies.length === 0 ? 40 : Math.max(0, 40 - lapsedPolicies.length * 10), max: 40 },
  ]);

  // 7. Engagement — inbound reply rate.
  const engagementScore = msgSummary.outbound > 0
    ? Math.min(100, Math.round((msgSummary.inbound / msgSummary.outbound) * 100))
    : (msgSummary.lastIn ? 60 : 20);

  const dims = [
    { key: 'communication',  label: 'Communication',      value: commScore,               detail: msgSummary.lastAny ? `${daysSinceLastAny}d since last touch` : 'no messages on file' },
    { key: 'renewal',        label: 'Renewal readiness',  value: renewalScore,            detail: complianceScore != null ? `compliance ${complianceScore}/100` : 'awaiting compliance score' },
    { key: 'referral',       label: 'Referral likelihood',value: referralScore,           detail: tenureDays ? `${Math.round(tenureDays/30)}mo tenure` : 'no active policy yet' },
    { key: 'crosssell',      label: 'Cross-sell potential', value: crosssellScore,        detail: `${missingCount} of 5 product lines missing` },
    { key: 'compliance',     label: 'Compliance completeness', value: complianceCompletenessScore, detail: 'blueprint #4' },
    { key: 'payment',        label: 'Payment stability',  value: paymentScore,            detail: `${activePolicies.length} active · ${lapsedPolicies.length} lapsed` },
    { key: 'engagement',     label: 'Engagement',         value: engagementScore,         detail: `${msgSummary.inbound} in · ${msgSummary.outbound} out` },
  ];

  const overall = Math.round(dims.reduce((s, d) => s + d.value, 0) / dims.length);
  const tone = overall >= 80 ? 'emerald' : overall >= 60 ? 'amber' : 'rose';
  const headerClass = tone === 'emerald' ? 'from-emerald-50 to-white border-emerald-300 text-emerald-900'
                    : tone === 'amber'   ? 'from-amber-50 to-white border-amber-300 text-amber-900'
                    :                       'from-rose-50 to-white border-rose-300 text-rose-900';

  return (
    <div className="space-y-3">
      <div className={`border-2 rounded-xl px-4 py-3 bg-gradient-to-b ${headerClass}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">Book-of-business score</div>
            <div className="text-3xl font-black tabular-nums">{overall}<span className="text-lg opacity-60">/100</span></div>
            <div className="text-[11px] mt-0.5 opacity-80">7-dimension health · blueprint module #18</div>
          </div>
          <div className="text-right text-[10px] opacity-70 tabular-nums">
            {dims.filter(d => d.value >= 70).length} strong · {dims.filter(d => d.value < 40).length} weak
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        {dims.map(d => {
          const v = d.value;
          const barTone = v >= 80 ? 'bg-emerald-500' : v >= 60 ? 'bg-amber-500' : v >= 40 ? 'bg-orange-500' : 'bg-rose-500';
          return (
            <div key={d.key} className="px-1">
              <div className="flex items-baseline justify-between text-[11.5px]">
                <span className="font-semibold text-slate-800">{d.label}</span>
                <span className="tabular-nums font-mono text-slate-600">{v}</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-0.5">
                <div className={`h-full ${barTone}`} style={{ width: `${v}%` }}/>
              </div>
              <div className="text-[10.5px] text-slate-500 mt-0.5">{d.detail}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
